import React, { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, Sparkle, CloudArrowUp, Check, Warning, ArrowRight, NumberCircleOne, NumberCircleTwo, NumberCircleThree } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ImportPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('chatgpt');
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError('');
    setResult(null);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await axios.post(`${API}/import/${tab}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Import failed. Please check your file format.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  }, [tab]);

  return (
    <div className="min-h-screen bg-transparent" data-testid="import-page">
      <div className="max-w-2xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <Link to="/app" className="w-8 h-8 rounded-lg flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-all" data-testid="import-back-button">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-xl font-semibold tracking-tight" style={{ fontFamily: 'Clash Display, sans-serif' }}>Import Conversations</h1>
            <p className="text-sm text-white/40">Bring your chats from ChatGPT or Claude</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {[
            { id: 'chatgpt', label: 'ChatGPT', color: 'from ChatGPT' },
            { id: 'claude', label: 'Claude', color: 'from Claude' }
          ].map(t => (
            <button key={t.id} onClick={() => { setTab(t.id); setResult(null); setError(''); }}
              className={`flex-1 py-3 rounded-xl text-sm font-medium transition-all ${
                tab === t.id ? 'bg-lime-400/15 text-lime-300 border border-lime-400/25' : 'bg-white/5 text-white/50 border border-white/10 hover:border-white/25'
              }`}
              data-testid={`import-tab-${t.id}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Instructions */}
        <motion.div key={tab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="border border-white/10 rounded-xl p-6 mb-6">
          <h2 className="text-base font-semibold mb-4" style={{ fontFamily: 'Clash Display, sans-serif' }}>
            How to export {tab === 'chatgpt' ? 'ChatGPT' : 'Claude'} data
          </h2>

          {tab === 'chatgpt' ? (
            <div className="space-y-4">
              <Step num={1} title="Open ChatGPT Settings">
                Go to <span className="font-mono text-xs bg-white/10 px-1.5 py-0.5 rounded">chatgpt.com</span> → Click your profile icon → <strong>Settings</strong>
              </Step>
              <Step num={2} title="Request Data Export">
                Go to <strong>Data Controls</strong> → Click <strong>Export data</strong> → Confirm via email
              </Step>
              <Step num={3} title="Upload the ZIP file">
                You'll receive an email with a download link. Upload the <span className="font-mono text-xs bg-white/10 px-1.5 py-0.5 rounded">.zip</span> file directly — no need to unzip it.
              </Step>
            </div>
          ) : (
            <div className="space-y-4">
              <Step num={1} title="Open Claude Settings">
                Go to <span className="font-mono text-xs bg-white/10 px-1.5 py-0.5 rounded">claude.ai</span> → Click your name → <strong>Settings</strong>
              </Step>
              <Step num={2} title="Export Your Data">
                Navigate to <strong>Account</strong> → Click <strong>Export Data</strong> → Confirm
              </Step>
              <Step num={3} title="Upload the JSON file">
                Download the export and upload the <span className="font-mono text-xs bg-white/10 px-1.5 py-0.5 rounded">conversations.json</span> file below.
              </Step>
            </div>
          )}
        </motion.div>

        {/* Upload Area */}
        <div className="border-2 border-dashed border-white/10 rounded-xl p-8 text-center hover:border-lime-400/40 transition-colors relative">
          <input
            type="file"
            accept={tab === 'chatgpt' ? '.json,.zip' : '.json'}
            onChange={handleUpload}
            className="absolute inset-0 opacity-0 cursor-pointer"
            disabled={uploading}
            data-testid="import-file-input"
          />
          {uploading ? (
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-2 border-lime-400/40 border-t-lime-600 rounded-full animate-spin" />
              <p className="text-sm text-white/50">Importing conversations...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3">
              <CloudArrowUp className="w-10 h-10 text-white/60" weight="duotone" />
              <p className="text-sm font-medium text-white/70">
                {tab === 'chatgpt'
                  ? <>Drop your <span className="font-mono text-xs bg-white/10 px-1.5 py-0.5 rounded">.zip</span> or <span className="font-mono text-xs bg-white/10 px-1.5 py-0.5 rounded">conversations.json</span> here</>
                  : <>Drop your <span className="font-mono text-xs bg-white/10 px-1.5 py-0.5 rounded">conversations.json</span> here</>
                }
              </p>
              <p className="text-xs text-white/40">or click to browse</p>
            </div>
          )}
        </div>

        {/* Result */}
        {result && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-4 flex items-center gap-3 bg-lime-400/10 border border-lime-400/25 rounded-xl p-4" data-testid="import-success">
            <Check weight="bold" className="w-5 h-5 text-lime-400 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-lime-300">{result.message}</p>
              <p className="text-xs text-lime-400 mt-0.5">Your conversations are ready in the sidebar.</p>
            </div>
            <button onClick={() => navigate('/app')} className="px-4 py-2 bg-lime-400/100 hover:bg-lime-600 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-1" data-testid="import-go-to-chat">
              Open chat <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </motion.div>
        )}

        {error && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-4 flex items-center gap-3 bg-red-400/10 border border-red-200 rounded-xl p-4" data-testid="import-error">
            <Warning weight="bold" className="w-5 h-5 text-red-400 shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </motion.div>
        )}
      </div>
    </div>
  );
}

function Step({ num, title, children }) {
  const icons = [NumberCircleOne, NumberCircleTwo, NumberCircleThree];
  const Icon = icons[num - 1] || NumberCircleOne;
  return (
    <div className="flex gap-3">
      <Icon weight="duotone" className="w-6 h-6 text-lime-500 shrink-0 mt-0.5" />
      <div>
        <p className="text-sm font-medium text-white/90">{title}</p>
        <p className="text-sm text-white/50 mt-0.5 leading-relaxed">{children}</p>
      </div>
    </div>
  );
}
